import { useCallback, useEffect } from 'react';

/**
 * Custom hook for managing ellipsis and scroll position in circular text containers.
 * Handles overflow detection and automatically resets scroll position to top.
 */
export const useCircularTextEllipsis = (containerRef: React.RefObject<HTMLDivElement>) => {
  const updateEllipsisAndResetScroll = useCallback(() => {
    if (!containerRef.current) return;

    const textWrapper = containerRef.current.querySelector('.tiptap-textblock-wrapper') as HTMLElement;
    if (!textWrapper) return;

    textWrapper.scrollTop = 0;

    const hasOverflow = textWrapper.scrollHeight > textWrapper.offsetHeight;
    const ellipsisDisplay = hasOverflow ? 'block' : 'none';
    textWrapper.style.setProperty('--show-ellipsis', ellipsisDisplay);
  }, [containerRef]);

  return { updateEllipsisAndResetScroll };
};

export const usePasteHandler = (containerRef: React.RefObject<HTMLDivElement>, isCircular: boolean, onPaste: () => void) => {
  useEffect(() => {
    if (!isCircular || !containerRef.current) return;

    const tiptapElement = containerRef.current.querySelector('.tiptap');
    if (!tiptapElement) return;

    const handlePaste = () => {
      // Small delay to ensure paste content is rendered before recalculating
      setTimeout(onPaste, 0);
    };

    tiptapElement.addEventListener('paste', handlePaste);
    return () => tiptapElement.removeEventListener('paste', handlePaste);
  }, [containerRef, isCircular, onPaste]);
};
